import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinner from '../../../components/LoadingSpinner';
import config from '../../../utils/config';
import logError from '../../../utils/errorUtil';
import { formatAsCurrency } from '../../../utils/formatAsCurrency';
import {
    RMA_ATTACHMENT_MAX_FILES,
    isValidRmaAttachmentFile,
    rmaAttachmentFileTooLarge,
} from '../../../utils/rma-attachment-validation.js';
import { useAuth } from '../../context/wms-context';
import { resolveRmaRole } from '../../utils/permission-groups';
import {
    approveRma,
    denyRma,
    getHistoryRmaDetail,
    getRmaReviewData,
    releaseRmaReview,
    saveRmaReview,
    submitRmaToSales,
    uploadRmaSubmissionDocuments,
} from './functions';
import CopyToClipboardButton from '../../../components/CopyToClipboardButton.js';

const defaultReviewForm = {
    shipMethodId: '',
    initialReturnReason: '',
    type: '',
    relatedDepartment: '',
    finalReturnReason: '',
    disposition: '',
    dispositionText: '',
    rmaInternalComments: '',
    rmaExternalComments: '',
    customerEmailMessage: '',
    reviewRestockPercent: 0,
    internalRmaOnly: false,
};

function roundCurrency(amount) {
    return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
}

function withQtyTotals(line, quantityToReturn) {
    if (quantityToReturn === '' || quantityToReturn == null) {
        return {
            ...line,
            quantityToReturn: '',
            linePrice: 0,
            linePriceDisplay: formatAsCurrency(0),
        };
    }
    const linePrice = roundCurrency((Number(line.invoiceLinePrice) || 0) * quantityToReturn);
    return {
        ...line,
        quantityToReturn,
        linePrice,
        linePriceDisplay: formatAsCurrency(linePrice),
    };
}

function clampQtyToReturn(quantityInput, maxQuantity) {
    const maxQty = Number(maxQuantity) || 0;
    const numericQuantity = Number(quantityInput);
    if (!Number.isFinite(numericQuantity)) return null;
    const flooredQuantity = Math.floor(numericQuantity);
    if (flooredQuantity < 1 || maxQty < 1) return null;
    return Math.min(flooredQuantity, maxQty);
}

function getReviewLineEditsError(lines) {
    const reviewLines = lines || [];
    const missingFields = [];

    for (const line of reviewLines) {
        const maxQuantity = Number(line.quantityOnRelease) || 0;
        const quantityToReturn = clampQtyToReturn(line.quantityToReturn, line.quantityOnRelease);
        const lineLabel = reviewLines.length > 1 ? `${line.partNumber || `Line ${line.id}`}: ` : '';

        if (quantityToReturn == null) {
            missingFields.push(
                maxQuantity >= 1
                    ? `${lineLabel}Qty to Return must be a whole number between 1 and ${maxQuantity}.`
                    : `${lineLabel}Qty to Return is required.`
            );
        }

        const missingReason = !String(line.reasonForReturn || '').trim();
        const missingDetails = !String(line.reasonForReturnComment || '').trim();
        if (missingReason && missingDetails) {
            missingFields.push(`${lineLabel}Return Reason and Details are required.`);
        } else if (missingReason) {
            missingFields.push(`${lineLabel}Return Reason is required.`);
        } else if (missingDetails) {
            missingFields.push(`${lineLabel}Return Reason Details are required.`);
        }
    }

    return missingFields.join('\n');
}

export default function WMS_RMAReview({ rmaId, onBack, readOnly = false }) {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [reviewData, setReviewData] = useState(null);
    const [reviewForm, setReviewForm] = useState(defaultReviewForm);
    const [submittingAction, setSubmittingAction] = useState('');
    const [actionModal, setActionModal] = useState(null);
    const [actionModalMessage, setActionModalMessage] = useState('');
    const [resultModal, setResultModal] = useState(null);
    const [documentUploadBusy, setDocumentUploadBusy] = useState(false);
    const documentUploadInputRef = useRef(null);

    useEffect(() => {
        loadRmaReviewData();
    }, [rmaId]);

    useEffect(() => {
        if (readOnly) return;

        const releaseUrl = `${config.origin}/wms/rma/rma-review-action`;
        const releasePayload = JSON.stringify({
            actionType: 'release',
            rmaId,
            reviewForm: {},
            authentication: {
                userId: currentUser?.authentication?.userId,
                userEmail: currentUser?.authentication?.userEmail,
                userCompany: currentUser?.authentication?.userCompany || localStorage.getItem('company'),
            },
        });

        const handleBeforeUnload = () => {
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
                navigator.sendBeacon(releaseUrl, new Blob([releasePayload], { type: 'application/json' }));
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            releaseRmaReview(rmaId, currentUser).catch((error) =>
                logError(
                    error,
                    `releaseRmaReview: Failed to release RMA review lock | rmaId: ${rmaId} | userId: ${
                        currentUser?.authentication?.userId || 'N/A'
                    }`,
                    false
                )
            );
        };
    }, [rmaId]);

    const loadRmaReviewData = async ({ quiet = false } = {}) => {
        try {
            if (!quiet) setLoading(true);
            const reviewResponse = readOnly
                ? await getHistoryRmaDetail(rmaId, currentUser)
                : await getRmaReviewData(rmaId, currentUser);
            if (!readOnly && reviewResponse?.locked) {
                onBack({
                    title: 'Record In Use',
                    desc: reviewResponse.message || 'This record is currently being processed by another user.',
                    status: 'error',
                });
                return;
            }
            setReviewData(reviewResponse);
            const savedReview = reviewResponse?.savedReview || {};
            setReviewForm({
                ...defaultReviewForm,
                ...savedReview,
                shipMethodId: savedReview.shipMethodId ?? reviewResponse?.header?.shipMethodId ?? '',
                initialReturnReason: savedReview.initialReturnReason ?? reviewResponse?.header?.initialReturnReason ?? '',
            });
        } catch (error) {
            logError(
                error,
                `loadRmaReviewData: Failed to load RMA review data | rmaId: ${rmaId} | userId: ${
                    currentUser?.authentication?.userId || 'N/A'
                } | userEmail: ${currentUser?.authentication?.userEmail || 'N/A'}`,
                false
            );
            setReviewData(null);
            setReviewForm(defaultReviewForm);
        } finally {
            if (!quiet) setLoading(false);
        }
    };

    const renderLinkedText = (label, value, href) => {
        const labelPrefix = label ? `${label}: ` : '';
        if (!value) return <p>{labelPrefix}</p>;
        if (!href)
            return (
                <p>
                    {labelPrefix}
                    {value}
                </p>
            );

        return (
            <p>
                {labelPrefix}
                <a className='text-blue-800 underline hover:text-blue' target='_blank' rel='noopener noreferrer' href={href}>
                    {value}
                </a>
            </p>
        );
    };

    const exitReviewAfterSuccess = async (toastPayload) => {
        await releaseRmaReview(rmaId, currentUser).catch(() => {});
        onBack(toastPayload);
    };

    const handleBack = async () => {
        // Release before navigating back: the parent refetches the list immediately on onBack(), and
        // that fetch would otherwise race the unmount effect's release — the row would stay 'Processing'
        // and disappear from the Pending list until a manual refresh.
        await exitReviewAfterSuccess();
    };

    if (loading) {
        return (
            <section className='flex justify-center items-center h-[calc(100vh-64px)]'>
                <LoadingSpinner className={'h-fit'} />
            </section>
        );
    }

    if (!reviewData?.header) {
        return (
            <section className='flex flex-col gap-4 justify-center items-center h-[calc(100vh-64px)]'>
                <h1 className='text-3xl'>RMA Review</h1>
                <p className='text-lg text-gray-700'>No review data found for this RMA.</p>
                <button className='px-3 py-1 text-white rounded-md bg-blue-primary hover:bg-blue-900' onClick={handleBack}>
                    Back to Pending RMAs
                </button>
            </section>
        );
    }

    const { header, lines, fieldOptions, orderProcessorUserNames } = reviewData;
    const linesTotal = (lines || []).reduce((sum, line) => sum + Number(line.linePrice || 0), 0);
    const selectedShipMethod = (fieldOptions?.shipMethodOptions || []).find(
        (shipMethod) => shipMethod.id === reviewForm.shipMethodId
    );
    const displayedShipMethodDescription = selectedShipMethod?.shipMethodDescription || header.shipMethodDescription || '';
    const displayedShipMethodAccountNumber =
        selectedShipMethod?.shipMethodAccountNumber || header.shipMethodAccountNumber || '';
    const rmaRole = resolveRmaRole(currentUser?.permissions || []);
    const isSalesState = Boolean(header?.isSales);
    const isManager = rmaRole === 'manager';
    const isOps = rmaRole === 'operations';
    const isSales = rmaRole === 'sales';
    const isReadOnly = rmaRole === 'read-only';
    const canEditCustomerSubmittedLines = isReadOnly && !isSalesState && !header?.operationsActedDate;

    const canEditFields = !readOnly && (isManager || (isOps && !isSalesState));
    const canEditLines =
        !readOnly && (isManager || (isOps && !isSalesState) || (isSales && isSalesState) || canEditCustomerSubmittedLines);
    const canSave = !readOnly && (canEditFields || (isSales && isSalesState) || canEditCustomerSubmittedLines);
    const canEditExternalComments = !readOnly && (canEditFields || (isSales && isSalesState));
    const canEditEmailMessage = !readOnly && (canEditFields || (isSales && isSalesState));
    const canEditRestockPercent = !readOnly && (isManager || (isSales && isSalesState));
    const canSubmitToSales =
        !readOnly && isOps && !isSalesState && String(reviewForm.relatedDepartment || '').toLowerCase() === 'sales';
    const canApprove = !readOnly && ((canEditFields && !canSubmitToSales) || (isSales && isSalesState));
    const canDenyToCustomer = !readOnly && canEditFields && !canSubmitToSales;
    const canReturnToOperations = !readOnly && isSales && isSalesState;
    const canUploadReviewDocuments = !readOnly && (isReadOnly || canEditFields);
    const updateReviewFormField = (fieldName, value) => {
        setReviewForm((previousFormData) => ({
            ...previousFormData,
            [fieldName]: value,
        }));
    };

    const updateReviewLine = (lineId, updater) => {
        setReviewData((previousData) => {
            if (!previousData?.lines) return previousData;
            return {
                ...previousData,
                lines: previousData.lines.map((line) => {
                    if (line.id !== lineId) return line;
                    return updater(line);
                }),
            };
        });
    };

    const updateLineQuantityToReturn = (lineId, quantityInput) => {
        updateReviewLine(lineId, (line) => {
            const maxQuantity = Number(line.quantityOnRelease) || 0;
            // Allow a blank field while typing; Number('') is 0 and would otherwise snap back to 1.
            if (quantityInput === '' || quantityInput === '-') {
                return withQtyTotals(line, '');
            }
            const numericQuantity = Number(quantityInput);
            if (!Number.isFinite(numericQuantity)) {
                return line;
            }
            const flooredQuantity = Math.floor(numericQuantity);
            if (flooredQuantity < 1) {
                return withQtyTotals(line, '');
            }
            const clampedQuantity = Math.min(flooredQuantity, maxQuantity || 1);
            return withQtyTotals(line, clampedQuantity);
        });
    };

    const commitLineQuantityToReturn = (lineId) => {
        updateReviewLine(lineId, (line) => {
            const clampedQuantity = clampQtyToReturn(line.quantityToReturn, line.quantityOnRelease);
            return withQtyTotals(line, clampedQuantity == null ? '' : clampedQuantity);
        });
    };

    const updateLineReasonForReturn = (lineId, reasonForReturn) => {
        updateReviewLine(lineId, (line) => ({ ...line, reasonForReturn }));
    };

    const updateLineReasonForReturnComment = (lineId, reasonForReturnComment) => {
        updateReviewLine(lineId, (line) => ({
            ...line,
            reasonForReturnComment: String(reasonForReturnComment || '').slice(0, 255),
        }));
    };

    const buildReviewPayload = (extra = {}) => ({
        ...reviewForm,
        ...extra,
        lines: (reviewData?.lines || []).map((line) => ({
            id: line.id,
            quantityToReturn: clampQtyToReturn(line.quantityToReturn, line.quantityOnRelease),
            reasonForReturn: line.reasonForReturn,
            reasonForReturnComment: line.reasonForReturnComment,
        })),
    });

    const validateReviewLineEdits = () => {
        if (!canEditLines) return true;
        const message = getReviewLineEditsError(reviewData?.lines);
        if (!message) return true;
        const errorCount = message.split('\n').length;
        setResultModal({
            title: errorCount > 1 ? 'Missing Required Fields' : 'Missing Required Field',
            message,
        });
        return false;
    };

    const handleReviewDocumentUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (documentUploadInputRef.current) documentUploadInputRef.current.value = '';
        if (!selectedFiles.length || !canUploadReviewDocuments) return;

        const invalidType = selectedFiles.filter((fileEntry) => !isValidRmaAttachmentFile(fileEntry));
        const tooLarge = selectedFiles.filter((fileEntry) => rmaAttachmentFileTooLarge(fileEntry));
        if (invalidType.length) {
            setResultModal({
                title: 'Invalid file type',
                message: 'Only PDF, JPG, JPEG, and PNG files are allowed (same rules as customer RMA upload).',
            });
            return;
        }
        if (tooLarge.length) {
            setResultModal({
                title: 'File too large',
                message: 'Each attachment must be 10 MB or less.',
            });
            return;
        }
        if (selectedFiles.length > RMA_ATTACHMENT_MAX_FILES) {
            setResultModal({
                title: 'Too many files',
                message: `You can upload up to ${RMA_ATTACHMENT_MAX_FILES} files at once.`,
            });
            return;
        }

        try {
            setDocumentUploadBusy(true);
            const uploadResponse = await uploadRmaSubmissionDocuments(rmaId, selectedFiles, currentUser);
            // Do not call getRma-review-data again here: while you hold the submission it stays
            // `Processing`, and that endpoint treats Processing as locked (another reviewer).
            setReviewData((previousData) => {
                if (!previousData?.header) return previousData;
                const nextPath = uploadResponse?.relatedDocumentsPath || previousData.header.relatedDocumentsPath;
                return {
                    ...previousData,
                    header: { ...previousData.header, relatedDocumentsPath: nextPath },
                };
            });
        } catch (error) {
            logError(
                error,
                `handleReviewDocumentUpload: Document upload failed | rmaId: ${rmaId} | userId: ${
                    currentUser?.authentication?.userId || 'N/A'
                }`,
                false
            );
            setResultModal({
                title: 'Upload failed',
                message: error?.message || 'Documents could not be uploaded.',
            });
        } finally {
            setDocumentUploadBusy(false);
        }
    };

    const runRmaAction = async (actionName, actionFn) => {
        try {
            setSubmittingAction(actionName);
            const actionResponse = await actionFn();
            if (actionResponse?.success === false) {
                setResultModal({
                    title: 'Error',
                    message: actionResponse.message || 'There was an error processing the request. Please try again.',
                });
                return { success: false };
            }
            return {
                success: true,
                message: actionResponse?.message || 'Action completed successfully.',
            };
        } catch (error) {
            logError(
                error,
                `runRmaAction: Failed to complete RMA action | actionName: ${actionName} | rmaId: ${rmaId} | userId: ${
                    currentUser?.authentication?.userId || 'N/A'
                }`,
                false
            );
            setResultModal({
                title: 'Error',
                message: error?.message || 'There was an error processing the request. Please try again.',
            });
            return { success: false };
        } finally {
            setSubmittingAction('');
        }
    };

    const handleSave = async () => {
        if (!validateReviewLineEdits()) return;
        const { success, message } = await runRmaAction('save', () =>
            saveRmaReview(rmaId, buildReviewPayload(), currentUser)
        );
        if (success) await exitReviewAfterSuccess({ title: 'Draft Saved', desc: message, status: 'success' });
    };

    const handleDeny = async (handoffMessage = '') => {
        if (!validateReviewLineEdits()) return;
        const reviewPayload = buildReviewPayload(handoffMessage ? { handoffMessage } : {});
        const { success, message } = await runRmaAction('deny', () => denyRma(rmaId, reviewPayload, currentUser));
        if (success) {
            await exitReviewAfterSuccess({
                title: canReturnToOperations ? 'Returned To Operations' : 'Request Denied',
                desc: message,
                status: 'success',
            });
        }
    };

    const handleApprove = async () => {
        if (!validateReviewLineEdits()) return;
        const { success, message } = await runRmaAction('approve', () =>
            approveRma(rmaId, buildReviewPayload(), currentUser)
        );
        if (success) await exitReviewAfterSuccess({ title: 'Request Approved', desc: message, status: 'success' });
    };

    const handleSubmitToSales = async (handoffMessage = '') => {
        if (!validateReviewLineEdits()) return;
        const reviewPayload = buildReviewPayload({ handoffMessage });
        const { success, message } = await runRmaAction('submitToSales', () =>
            submitRmaToSales(rmaId, reviewPayload, currentUser)
        );
        if (success) await exitReviewAfterSuccess({ title: 'Submitted To Sales', desc: message, status: 'success' });
    };

    const handleConfirmAction = async () => {
        const confirmedAction = actionModal?.action;
        setActionModal(null);
        const handoffMessageValue = actionModalMessage;
        setActionModalMessage('');
        if (confirmedAction === 'deny') {
            if (canReturnToOperations) {
                await handleDeny(handoffMessageValue);
                return;
            }
            // If Internal RMA Only is checked, do not require a customer email message.
            if (reviewForm.internalRmaOnly) {
                await handleDeny('');
                return;
            }
            if (!reviewForm.customerEmailMessage.trim()) {
                setResultModal({
                    title: 'Missing Required Field',
                    message: 'Email Message to Customer is required before denying this request.',
                });
                return;
            }
            await handleDeny('');
        } else if (confirmedAction === 'submit-to-sales') {
            if (!reviewForm?.type?.trim()) {
                setResultModal({
                    title: 'Missing Required Field',
                    message: 'Type is required before submitting this request to Sales.',
                });
                return;
            }
            await handleSubmitToSales(handoffMessageValue);
        } else if (confirmedAction === 'approve') {
            if (!reviewForm?.type?.trim()) {
                setResultModal({
                    title: 'Missing Required Field',
                    message: 'Type is required before approving this request.',
                });
                return;
            }
            await handleApprove();
        }
    };

    const sectionHeaderClassName = 'px-2 py-1 text-sm font-bold text-left bg-gray-200 border border-gray-300';
    const labelCellClassName = 'px-2 py-1 w-44 text-sm font-semibold border border-gray-300 bg-gray-50';
    const valueCellClassName = 'px-2 py-1 text-sm border border-gray-300';
    const inputClassName =
        'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400';
    const selectClassName =
        'w-full px-2 py-1 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400';

    return (
        <section className='px-8 pt-8 pb-10 w-full h-[calc(100vh-64px)] overflow-y-auto'>
            <header className='flex justify-between items-center pb-4'>
                <div className='flex gap-4 items-center'>
                    <h1 className='text-2xl font-bold text-blue-primary'>RMA Review</h1>
                    {header.createdRmaName ? (
                        <>
                            <h1 className='text-xl font-bold'>
                                {renderLinkedText('', header.createdRmaName, header.rmaLink)}
                            </h1>
                            <CopyToClipboardButton toCopy={header.createdRmaName} label='RMA Number' />
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                <button className='px-3 py-1 text-white rounded-md bg-blue-primary hover:bg-blue-900' onClick={handleBack}>
                    Back to Pending RMAs
                </button>
            </header>

            <section className='mb-4 border border-gray-300'>
                <h2 className={sectionHeaderClassName}>CUSTOMER SUBMISSION</h2>
                <table className='w-full border-collapse table-fixed'>
                    <colgroup>
                        <col className='w-44' />
                        <col />
                        <col className='w-44' />
                        <col />
                    </colgroup>
                    <tbody>
                        <tr>
                            <td className={labelCellClassName}>Account Name</td>
                            <td className={valueCellClassName}>
                                {renderLinkedText('', header.accountName, header.accountLink)}
                            </td>
                            <td className={labelCellClassName}>Contact</td>
                            <td className={valueCellClassName}>
                                {renderLinkedText('', header.contactName, header.contactLink)}
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Invoice</td>
                            <td className={valueCellClassName}>
                                {renderLinkedText('', header.invoiceName, header.invoiceLink)}
                            </td>
                            <td className={labelCellClassName}>Phone</td>
                            <td className={valueCellClassName}>{header.contactPhone || ''}</td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Sales Order</td>
                            <td className={valueCellClassName}>
                                {renderLinkedText('', header.salesOrderName, header.salesOrderLink)}
                            </td>
                            <td className={labelCellClassName}>Email</td>
                            <td className={valueCellClassName}>{header.contactEmail || ''}</td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Release</td>
                            <td className={valueCellClassName}>
                                {renderLinkedText('', header.releaseName, header.releaseLink)}
                            </td>
                            <td className={labelCellClassName}>Customer PO</td>
                            <td className={valueCellClassName}>{header.customerPoNumber || ''}</td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Ship Method</td>
                            <td className={valueCellClassName}>
                                <select
                                    className={selectClassName}
                                    value={reviewForm.shipMethodId}
                                    onChange={(event) => updateReviewFormField('shipMethodId', event.target.value)}
                                    disabled={!canEditFields}
                                >
                                    <option value=''></option>
                                    {(fieldOptions?.shipMethodOptions || []).map((shipMethod) => (
                                        <option key={shipMethod.id} value={shipMethod.id}>
                                            {shipMethod.name || shipMethod.shipMethodDescription || shipMethod.id}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className={labelCellClassName}>Order Processed By</td>
                            <td className={valueCellClassName}>{orderProcessorUserNames.join('/')}</td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Ship Method Description</td>
                            <td className={valueCellClassName}>{displayedShipMethodDescription}</td>
                            <td className={labelCellClassName}>Acct #</td>
                            <td className={valueCellClassName}>{displayedShipMethodAccountNumber}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section className='mb-4 border border-gray-300'>
                <h2 className={sectionHeaderClassName}>PARTS</h2>
                <article className='overflow-auto'>
                    <table className='w-full border-collapse'>
                        <thead>
                            <tr className='text-sm text-left bg-gray-100'>
                                <th className='px-2 py-1 border border-gray-300'>Part Number</th>
                                <th className='px-2 py-1 border border-gray-300'>Keyword</th>
                                <th className='px-2 py-1 border border-gray-300'>Serial Number</th>
                                <th className='px-2 py-1 border border-gray-300'>Condition Code</th>
                                <th className='px-2 py-1 border border-gray-300'>Quantity on Release</th>
                                <th className='px-2 py-1 border border-gray-300'>Qty to Return</th>
                                <th className='px-2 py-1 border border-gray-300'>Price Paid</th>
                                <th className='px-2 py-1 border border-gray-300'>Total Return</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(lines || []).map((line) => (
                                <React.Fragment key={line.id}>
                                    <tr className='text-sm'>
                                        <td className='px-2 py-1 border border-gray-300'>{line.partNumber || ''}</td>
                                        <td className='px-2 py-1 border border-gray-300'>{line.keyword || ''}</td>
                                        <td className='px-2 py-1 border border-gray-300'>{line.serialNumber || ''}</td>
                                        <td className='px-2 py-1 border border-gray-300'>{line.conditionCode || ''}</td>
                                        <td className='px-2 py-1 border border-gray-300'>{line.quantityOnRelease || 0}</td>
                                        <td className='px-2 py-1 border border-gray-300'>
                                            {canEditLines ? (
                                                <input
                                                    type='number'
                                                    min='1'
                                                    max={line.quantityOnRelease || 1}
                                                    value={line.quantityToReturn === '' ? '' : line.quantityToReturn}
                                                    disabled={Boolean(submittingAction)}
                                                    onChange={(event) =>
                                                        updateLineQuantityToReturn(line.id, event.target.value)
                                                    }
                                                    onBlur={() => commitLineQuantityToReturn(line.id)}
                                                    className='px-2 py-1 w-20 text-right bg-white rounded border border-gray-300 disabled:bg-gray-100'
                                                />
                                            ) : (
                                                line.quantityToReturn || 0
                                            )}
                                        </td>
                                        <td className='px-2 py-1 border border-gray-300'>{line.pricePaidDisplay}</td>
                                        <td className='px-2 py-1 border border-gray-300'>{line.linePriceDisplay}</td>
                                    </tr>
                                    <tr className='text-sm'>
                                        <td className='px-2 py-1 font-semibold border border-gray-300 align-middle whitespace-nowrap'>
                                            Reason
                                        </td>
                                        <td className='px-2 py-1 border border-gray-300 align-middle' colSpan={1}>
                                            {canEditLines ? (
                                                <select
                                                    value={line.reasonForReturn || ''}
                                                    disabled={Boolean(submittingAction)}
                                                    onChange={(event) =>
                                                        updateLineReasonForReturn(line.id, event.target.value)
                                                    }
                                                    className='px-2 py-1 w-52 max-w-full text-sm bg-white rounded border border-gray-300 disabled:bg-gray-100'
                                                >
                                                    <option value='' disabled></option>
                                                    {fieldOptions.lineReturnReasonOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                line.reasonForReturn || ''
                                            )}
                                        </td>
                                        <td className='px-2 py-1 font-semibold border border-gray-300 align-middle whitespace-nowrap'>
                                            Details
                                        </td>
                                        <td
                                            className='px-2 py-1 whitespace-pre-wrap break-words border border-gray-300 align-middle'
                                            colSpan={5}
                                        >
                                            {canEditLines ? (
                                                <div className='relative'>
                                                    <textarea
                                                        value={line.reasonForReturnComment || ''}
                                                        onChange={(event) =>
                                                            updateLineReasonForReturnComment(line.id, event.target.value)
                                                        }
                                                        disabled={Boolean(submittingAction)}
                                                        className={`${inputClassName} min-h-[50px] disabled:bg-gray-100`}
                                                        rows={2}
                                                        maxLength={255}
                                                        placeholder='Enter details'
                                                    />
                                                    <span className='absolute right-2 bottom-1 text-[10px] text-gray-500'>
                                                        {(line.reasonForReturnComment || '').length}/255
                                                    </span>
                                                </div>
                                            ) : (
                                                line.reasonForReturnComment || ''
                                            )}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                            <tr className='text-sm font-semibold bg-gray-100'>
                                <td className='px-2 py-1 text-right border border-gray-300' colSpan={7}>
                                    TOTAL
                                </td>
                                <td className='px-2 py-1 border border-gray-300'>{formatAsCurrency(linesTotal)}</td>
                            </tr>
                        </tbody>
                    </table>
                </article>
            </section>

            <section className='mb-4 border border-gray-300'>
                <h2 className={sectionHeaderClassName}>Documents</h2>
                <article className='px-2 py-2 text-sm border-t border-gray-300'>
                    <p className='mb-1'>
                        Related Documents Path:{' '}
                        {header.relatedDocumentsPath ? (
                            <a
                                className='text-blue-800 underline hover:text-blue'
                                href={header.relatedDocumentsPath}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {header.relatedDocumentsPath}
                            </a>
                        ) : (
                            ''
                        )}
                    </p>
                    {canUploadReviewDocuments ? (
                        <div className='flex flex-wrap gap-2 items-center mb-3'>
                            <input
                                ref={documentUploadInputRef}
                                type='file'
                                multiple
                                accept='.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
                                className='hidden'
                                onChange={handleReviewDocumentUpload}
                            />
                            <button
                                type='button'
                                className='px-3 py-1 text-sm text-white rounded bg-blue-primary hover:bg-blue-900 disabled:opacity-60'
                                disabled={documentUploadBusy || Boolean(submittingAction)}
                                onClick={() => documentUploadInputRef.current?.click()}
                            >
                                {documentUploadBusy ? 'Uploading…' : 'Add documents'}
                            </button>
                            <span className='text-xs text-gray-600'>
                                PDF / JPG / PNG, up to {RMA_ATTACHMENT_MAX_FILES} files, 10&nbsp;MB each.
                            </span>
                        </div>
                    ) : null}
                    <p className='mt-2 text-gray-700'>
                        {header.relatedDocumentsPath
                            ? 'Open the Dropbox link above to view uploaded documents (PDF and images).'
                            : 'No uploaded client documents path on file yet.'}
                    </p>
                </article>
            </section>

            <section className='mb-4 border border-gray-300'>
                <h2 className={sectionHeaderClassName}>COMMENTS</h2>
                <table className='w-full border-collapse'>
                    <tbody>
                        <tr>
                            <td className={labelCellClassName}>Sales Order Internal</td>
                            <td className={`whitespace-pre-wrap ${valueCellClassName}`}>
                                {header.salesOrderInternalComments || ''}
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Sales Order External</td>
                            <td className={`whitespace-pre-wrap ${valueCellClassName}`}>
                                {header.salesOrderExternalComments || ''}
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Release Internal</td>
                            <td className={`whitespace-pre-wrap ${valueCellClassName}`}>
                                {header.releaseInternalComments || ''}
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Release External</td>
                            <td className={`whitespace-pre-wrap ${valueCellClassName}`}>
                                {header.releaseExternalComments || ''}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section className='mb-4 border border-gray-300'>
                <h2 className={sectionHeaderClassName}>RMA REVIEW</h2>
                <table className='w-full border-collapse'>
                    <tbody>
                        <tr>
                            <td className={labelCellClassName}>Type *</td>
                            <td className={valueCellClassName}>
                                <select
                                    className={selectClassName}
                                    value={reviewForm.type}
                                    onChange={(event) => updateReviewFormField('type', event.target.value)}
                                    disabled={!canEditFields}
                                    required
                                >
                                    <option value=''></option>
                                    {fieldOptions.typeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className={labelCellClassName}>Initial Return Reason</td>
                            <td className={valueCellClassName}>
                                <select
                                    className={selectClassName}
                                    value={reviewForm.initialReturnReason}
                                    onChange={(event) => updateReviewFormField('initialReturnReason', event.target.value)}
                                    disabled={!canEditFields}
                                >
                                    <option value=''></option>
                                    {fieldOptions.initialReturnReasonOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Final Return Reason</td>
                            <td className={valueCellClassName} colSpan={3}>
                                <select
                                    className={selectClassName}
                                    value={reviewForm.finalReturnReason}
                                    onChange={(event) => updateReviewFormField('finalReturnReason', event.target.value)}
                                    disabled={!canEditFields}
                                >
                                    <option value=''></option>
                                    {fieldOptions.finalReturnReasonOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Related Department</td>
                            <td className={valueCellClassName}>
                                <select
                                    className={selectClassName}
                                    value={reviewForm.relatedDepartment}
                                    onChange={(event) => updateReviewFormField('relatedDepartment', event.target.value)}
                                    disabled={!canEditFields}
                                >
                                    <option value=''></option>
                                    {fieldOptions.relatedDepartmentOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className={labelCellClassName}>Disposition</td>
                            <td className={valueCellClassName}>
                                <select
                                    className={selectClassName}
                                    value={reviewForm.disposition}
                                    onChange={(event) => updateReviewFormField('disposition', event.target.value)}
                                    disabled={!canEditFields}
                                >
                                    <option value=''></option>
                                    {fieldOptions.dispositionOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Disposition Details</td>
                            <td className={valueCellClassName} colSpan={3}>
                                <textarea
                                    className={`${inputClassName} min-h-[60px]`}
                                    maxLength={255}
                                    value={reviewForm.dispositionText}
                                    onChange={(event) => updateReviewFormField('dispositionText', event.target.value)}
                                    disabled={!canEditFields}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>RMA Internal Comments</td>
                            <td className={valueCellClassName} colSpan={3}>
                                <input
                                    className={inputClassName}
                                    value={reviewForm.rmaInternalComments}
                                    onChange={(event) => updateReviewFormField('rmaInternalComments', event.target.value)}
                                    disabled={!canSave}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>RMA External Comments</td>
                            <td className={valueCellClassName} colSpan={3}>
                                <input
                                    className={inputClassName}
                                    value={reviewForm.rmaExternalComments}
                                    onChange={(event) => updateReviewFormField('rmaExternalComments', event.target.value)}
                                    disabled={!canEditExternalComments}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Restock Fee %</td>
                            <td className={valueCellClassName} colSpan={3}>
                                <input
                                    className={inputClassName}
                                    type='number'
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={reviewForm.reviewRestockPercent}
                                    onChange={(event) => {
                                        const val = Math.min(100, Math.max(0, parseInt(event.target.value, 10) || 0));
                                        event.target.value = val;
                                        updateReviewFormField('reviewRestockPercent', val);
                                    }}
                                    disabled={!canEditRestockPercent}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td className={labelCellClassName}>Email Message to Customer</td>
                            <td className={valueCellClassName} colSpan={3}>
                                <textarea
                                    className={`${inputClassName} min-h-[80px]`}
                                    value={reviewForm.customerEmailMessage}
                                    onChange={(event) => updateReviewFormField('customerEmailMessage', event.target.value)}
                                    disabled={!canEditEmailMessage}
                                />
                                <p className='mt-1 text-xs text-gray-600'>Note: Required if RMA request is denied and Internal RMA Only is not checked.</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className='flex gap-3 justify-end px-2 py-2 border-t border-gray-300'>
                    <div className='flex w-full justify-start'>
                        <label
                            className={`flex items-center gap-2 text-sm font-medium text-gray-700 ${
                                canEditEmailMessage ? 'cursor-pointer' : 'cursor-not-allowed'
                            }`}
                        >
                            <input
                                type='checkbox'
                                name='internalRmaOnly'
                                checked={reviewForm.internalRmaOnly}
                                onChange={(event) => updateReviewFormField('internalRmaOnly', event.target.checked)}
                                disabled={!canEditEmailMessage}
                                className='w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400'
                            />
                            <span>INTERNAL RMA ONLY</span>
                        </label>
                    </div>
                    {canSave ? (
                        <button
                            className='px-4 py-1 text-white rounded bg-blue-primary hover:bg-blue-900 disabled:opacity-60 whitespace-nowrap'
                            onClick={handleSave}
                            disabled={Boolean(submittingAction)}
                        >
                            {submittingAction === 'save' ? 'Saving...' : 'Save'}
                        </button>
                    ) : null}
                    {canSubmitToSales ? (
                        <button
                            className='px-4 py-1 text-white rounded bg-indigo-700 hover:bg-indigo-800 disabled:opacity-60 whitespace-nowrap'
                            onClick={() => {
                                if (!validateReviewLineEdits()) return;
                                setActionModalMessage('');
                                setActionModal({
                                    action: 'submit-to-sales',
                                    title: 'Submit To Sales',
                                    message: 'Send this RMA to Sales review and include a message?',
                                    confirmLabel: 'Submit To Sales',
                                    requiresHandoffMessage: true,
                                });
                            }}
                            disabled={Boolean(submittingAction)}
                        >
                            {submittingAction === 'submitToSales' ? 'Submitting...' : 'Submit to Sales'}
                        </button>
                    ) : null}
                    {canDenyToCustomer || canReturnToOperations ? (
                        <button
                            className='px-4 py-1 text-white bg-red-700 rounded hover:bg-red-800 disabled:opacity-60 whitespace-nowrap'
                            onClick={() => {
                                if (!validateReviewLineEdits()) return;
                                setActionModalMessage('');
                                setActionModal({
                                    action: 'deny',
                                    title: canReturnToOperations ? 'Return To Operations' : 'Confirm Deny Request',
                                    message: canReturnToOperations
                                        ? 'Return this RMA to Operations review and include a message?'
                                        : `Deny this RMA request${
                                            reviewForm.internalRmaOnly ? '?' : ' and notify the customer?'
                                        }`,
                                    confirmLabel: canReturnToOperations ? 'Return To Operations' : 'Deny Request',
                                    requiresHandoffMessage: canReturnToOperations,
                                });
                            }}
                            disabled={Boolean(submittingAction)}
                        >
                            {submittingAction === 'deny'
                                ? canReturnToOperations
                                    ? 'Returning...'
                                    : 'Denying...'
                                : canReturnToOperations
                                ? 'Return To Operations'
                                : 'Deny Request'}
                        </button>
                    ) : null}
                    {canApprove ? (
                        <button
                            className='px-4 py-1 text-white bg-green-700 rounded hover:bg-green-800 disabled:opacity-60 whitespace-nowrap'
                            onClick={() => {
                                if (!validateReviewLineEdits()) return;
                                setActionModal({
                                    action: 'approve',
                                    title: 'Confirm Approve Request',
                                    message: `Approve this RMA request${
                                        reviewForm.internalRmaOnly ? '' : ', send the approval email,'
                                    } and finalize it?`,
                                    confirmLabel: 'Approve Request',
                                });
                            }}
                            disabled={Boolean(submittingAction)}
                        >
                            {submittingAction === 'approve' ? 'Approving...' : 'Approve Request'}
                        </button>
                    ) : null}
                </div>
            </section>

            {actionModal ? (
                <section className='flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-60'>
                    <div className='p-4 w-full max-w-md bg-white rounded shadow-lg'>
                        <h3 className='mb-2 text-lg font-semibold'>{actionModal.title}</h3>
                        <p className='mb-4 text-sm text-gray-700'>{actionModal.message}</p>
                        {actionModal.requiresHandoffMessage ? (
                            <textarea
                                className={`${inputClassName} mb-4 min-h-[100px]`}
                                value={actionModalMessage}
                                onChange={(event) => setActionModalMessage(event.target.value)}
                                placeholder='Enter message to include in the handoff email...'
                            />
                        ) : null}
                        <div className='flex gap-2 justify-end'>
                            <button
                                className='px-3 py-1 text-gray-700 rounded border border-gray-300 hover:bg-gray-100'
                                onClick={() => {
                                    setActionModal(null);
                                    setActionModalMessage('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className='px-3 py-1 text-white rounded bg-blue-primary hover:bg-blue-900'
                                onClick={handleConfirmAction}
                            >
                                {actionModal.confirmLabel}
                            </button>
                        </div>
                    </div>
                </section>
            ) : null}

            {resultModal ? (
                <section className='flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-60'>
                    <div className='p-4 w-full max-w-md bg-white rounded shadow-lg'>
                        <h3 className='mb-2 text-lg font-semibold'>{resultModal.title}</h3>
                        <p className='mb-4 text-sm text-gray-700 whitespace-pre-line'>{resultModal.message}</p>
                        <div className='flex justify-end'>
                            <button
                                className='px-3 py-1 text-white rounded bg-blue-primary hover:bg-blue-900'
                                onClick={() => setResultModal(null)}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </section>
            ) : null}
        </section>
    );
}
